import { useEffect, useMemo, useRef, useState } from "react";
import { fetchListadoTecnicos } from "./useTecnicosApi";

import { imageUrl } from '../../../config/api';
const PAGE_SIZE = 8;
const VIEW_STATE_KEY = "tecnicos:viewState:v1";

function readStoredViewState() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(VIEW_STATE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_error) {
        return null;
    }
}

function writeStoredViewState(payload) {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(payload));
    } catch (_error) {
        // Ignore sessionStorage write errors (private mode/quota)
    }
}

function normalizeText(value) {
    return String(value || "")
        .toLocaleLowerCase("es")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function imageUrlFor(nombre) {
    return imageUrl(nombre);
}

export default function useTecnicosData() {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [searchTerm, setSearchTerm] = useState(() => readStoredViewState()?.searchTerm ?? "");
    const [tecnicos, setTecnicos] = useState([]);
    const [currentPage, setCurrentPage] = useState(() => {
        const stored = readStoredViewState()?.currentPage;
        return Number.isInteger(stored) && stored > 0 ? stored : 1;
    });
    const isFirstSearchRender = useRef(true);

    useEffect(() => {
        let isMounted = true;

        async function loadTecnicos() {
            setIsError(false);

            try {
                const listado = await fetchListadoTecnicos();

                if (!isMounted) {
                    return;
                }

                setTecnicos(
                    listado.map((tecnico) => ({
                        ...tecnico,
                        rol: "Técnico",
                        imageUrl: imageUrlFor(tecnico.nombre),
                    }))
                );
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                setIsError(true);
                setTecnicos([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadTecnicos();

        return () => {
            isMounted = false;
        };
    }, []);

    const filteredTecnicos = useMemo(() => {
        const normalizedSearch = normalizeText(searchTerm).trim();

        if (!normalizedSearch) {
            return tecnicos;
        }

        return tecnicos.filter((tecnico) => {
            const nombre = normalizeText(tecnico.nombre);
            const nacionalidad = normalizeText(tecnico.nacionalidad);

            return nombre.includes(normalizedSearch) || nacionalidad.includes(normalizedSearch);
        });
    }, [tecnicos, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredTecnicos.length / PAGE_SIZE));
    const safeCurrentPage = Math.min(currentPage, totalPages);

    const paginatedTecnicos = useMemo(() => {
        const start = (safeCurrentPage - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        return filteredTecnicos.slice(start, end);
    }, [filteredTecnicos, safeCurrentPage]);

    const paginatedTecnicosWithMeta = paginatedTecnicos;

    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        if (tecnicos.length === 0) {
            return;
        }
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages, tecnicos.length]);

    useEffect(() => {
        writeStoredViewState({ searchTerm, currentPage });
    }, [searchTerm, currentPage]);

    const pageNumbers = useMemo(() => {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }, [totalPages]);

    return {
        isLoading,
        isError,
        hasData: tecnicos.length > 0,
        searchTerm,
        setSearchTerm,
        tecnicos: paginatedTecnicosWithMeta,
        totalTecnicos: tecnicos.length,
        filteredTotal: filteredTecnicos.length,
        currentPage: safeCurrentPage,
        totalPages,
        pageNumbers,
        onPageChange: setCurrentPage,
    };
}
